/* eslint-disable camelcase, @typescript-eslint/naming-convention -- mirrors the Rails JSON contract */
import React from 'react';
import { getCurrentLanguage, translate } from '@utils/i18n';
import { BarList, Button, Text } from '@ui';
import { Status } from '@ui/atoms/Status';
import { Brushaway, BrushawayBody, BrushawayFooter, BrushawayHeader } from '@ui/molecules/Brushaway';
import { ChartView } from '@ui/molecules/ChartView';
import { StatCard } from '@ui/molecules/StatCard';
import { useTechnicianScorecard } from '../../hooks/useTechnicianScorecard';
import PerformanceFlagCard from '../PerformanceFlag';
import { TECHNICIAN_RATING_STATUS_COLOR, technicianRatingLabel } from '../../utils/technicianRatingDisplay';
import './TechnicianScorecardDrawerStyles.scss';
const NS = 'features.iim_review.technician_scorecard_drawer';
const ISSUE_TYPE_LABELS_NS = 'features.iim_review.issues_grid.issue_type_labels';
const ISSUE_TYPE_BREAKDOWN_LIMIT = 5;
function IssueTypeBreakdownChart({ rows }) {
    if (rows.length === 0)
        return null;
    // rows arrive sorted desc by count; a single dominant issue type (e.g. thousands of
    // unanswered questions vs. dozens of everything else) makes every other bar unreadably
    // small, so this shows only the top offenders — matching the "focus coaching here" intent.
    const topRows = rows.slice(0, ISSUE_TYPE_BREAKDOWN_LIMIT);
    const items = topRows.map((row) => ({
        label: translate(row.issueType, { namespace: ISSUE_TYPE_LABELS_NS, defaultValue: row.issueType }),
        value: row.count
    }));
    return (React.createElement("section", { className: "technician-scorecard-drawer__section technician-scorecard-drawer__section--chart" },
        React.createElement(Text, { variant: "x-small", as: "h4", className: "technician-scorecard-drawer__section-title" }, translate('issue_breakdown', { namespace: NS, defaultValue: 'Issue Type Breakdown' })),
        React.createElement(BarList, { items: items, color: "#3D1B9D" })));
}
const KPI_ORDER_LINE_1 = ['resolutionRate', 'avgResubmissions', 'numberOfInspections'];
const KPI_ORDER_LINE_2 = [
    'deficienciesPerInspection',
    'avgCompletionTimeVsBookedMinutes',
    'callbacksReturnVisits'
];
const KPI_LABEL_DEFAULTS = {
    numberOfInspections: 'Number of Inspections',
    resolutionRate: 'Resolution Rate',
    avgResubmissions: 'Avg Resubmissions',
    deficienciesPerInspection: 'Deficiencies / Inspection',
    avgCompletionTimeVsBookedMinutes: 'Avg Completion Time vs. Booked',
    callbacksReturnVisits: 'Callbacks / Return Visits'
};
/**
 * Whether a rising value is good, bad, or neither, for coloring the delta. Number of
 * inspections is purely informational — more or fewer isn't itself good or bad.
 */
const KPI_DIRECTION = {
    numberOfInspections: 'neutral',
    resolutionRate: 'higher_is_better',
    avgResubmissions: 'lower_is_better',
    deficienciesPerInspection: 'lower_is_better',
    avgCompletionTimeVsBookedMinutes: 'lower_is_better',
    callbacksReturnVisits: 'lower_is_better'
};
function formatKpiValue(key, value) {
    if (value == null)
        return '—';
    if (key === 'resolutionRate')
        return `${value}%`;
    if (key === 'avgCompletionTimeVsBookedMinutes')
        return `${value > 0 ? '+' : ''}${value}m`;
    return String(value);
}
function computeKpiDelta(metricKey, value, previousValue) {
    if (value == null || previousValue == null)
        return null;
    const diff = Math.round((value - previousValue) * 10) / 10;
    if (diff === 0) {
        return {
            text: translate('no_change', { namespace: NS, defaultValue: 'No change vs last month' }),
            direction: 'neutral',
            sentiment: 'neutral'
        };
    }
    const direction = KPI_DIRECTION[metricKey];
    const trendDirection = diff > 0 ? 'up' : 'down';
    const isImprovement = diff > 0 === (direction === 'higher_is_better');
    let sentiment = 'neutral';
    if (direction !== 'neutral') {
        sentiment = isImprovement ? 'positive' : 'negative';
    }
    return {
        text: translate('delta_vs_last_month', {
            namespace: NS,
            delta: `${diff > 0 ? '+' : ''}${diff}`,
            defaultValue: '%{delta} vs last month'
        }),
        direction: trendDirection,
        sentiment
    };
}
function KpiTile({ metricKey, metric }) {
    const delta = computeKpiDelta(metricKey, metric.value, metric.previousValue);
    return (React.createElement(StatCard, { variant: "static", value: formatKpiValue(metricKey, metric.value), label: translate(metricKey, { namespace: `${NS}.kpi_labels`, defaultValue: KPI_LABEL_DEFAULTS[metricKey] }), trend: delta === null || delta === void 0 ? void 0 : delta.text, trendDirection: delta === null || delta === void 0 ? void 0 : delta.direction, trendSentiment: delta === null || delta === void 0 ? void 0 : delta.sentiment }));
}
function KpiTileGrid({ kpis, kpiShowLines }) {
    return (React.createElement("section", { className: "technician-scorecard-drawer__kpi-grid" }, kpiShowLines.map((key) => (React.createElement(KpiTile, { key: key, metricKey: key, metric: kpis[key] })))));
}
function WeeklyIssuesTrendChart({ rows }) {
    if (rows.length === 0)
        return null;
    const hasActivity = rows.some((row) => row.detected > 0 || row.dismissed > 0);
    const chartBlock = {
        type: 'chart',
        block_id: 'technician-scorecard-weekly-trend',
        block_index: 0,
        chart_type: 'line',
        data: {
            labels: rows.map((row) => row.weekStart),
            datasets: [
                {
                    label: translate('issues_detected', { namespace: NS, defaultValue: 'Issues Detected' }),
                    data: rows.map((row) => row.detected)
                },
                {
                    label: translate('issues_dismissed', { namespace: NS, defaultValue: 'Dismissed' }),
                    data: rows.map((row) => row.dismissed)
                }
            ]
        },
        options: { show_legend: true, show_values: false, y_axis_major_unit: 1 }
    };
    return (React.createElement("section", { className: "technician-scorecard-drawer__section technician-scorecard-drawer__section--chart" },
        React.createElement(Text, { variant: "x-small", as: "h4", className: "technician-scorecard-drawer__section-title" }, translate('weekly_trend', { namespace: NS, defaultValue: 'Issues Detected vs Dismissed' })),
        hasActivity ? (React.createElement(ChartView, { block: chartBlock, className: "technician-scorecard-drawer__chart-view" })) : (React.createElement(Text, { variant: "body", as: "p", className: "technician-scorecard-drawer__empty-state" }, translate('no_recent_activity', {
            namespace: NS,
            defaultValue: 'No issues detected in the last 4 weeks.'
        })))));
}
const PERFORMANCE_FLAG_ICONS = {
    recurring_deficiency_pattern: 'fa-triangle-exclamation',
    documentation_gaps: 'fa-file-lines',
    asset_count_change: 'fa-building'
};
const PERFORMANCE_FLAG_LABELS_NS = `${NS}.performance_flags_labels`;
const MS_PER_DAY = 86400000;
function flagT(key, defaultValue, params = {}) {
    return translate(key, Object.assign({ namespace: PERFORMANCE_FLAG_LABELS_NS, defaultValue }, params));
}
/** "January 2026" in the current locale, from an ISO date (read as UTC so the day never shifts). */
function formatMonthYear(isoDate) {
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime()))
        return null;
    return date.toLocaleDateString(getCurrentLanguage(), { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
/** MM-DD-YYYY, matching the `sinceDate` format of the asset count flag. */
function formatUsDate(isoDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
    return match ? `${match[2]}-${match[3]}-${match[1]}` : null;
}
function daysSince(isoDate) {
    const time = new Date(isoDate).getTime();
    if (Number.isNaN(time))
        return null;
    return Math.max(0, Math.floor((Date.now() - time) / MS_PER_DAY));
}
function joinMeta(parts) {
    const present = parts.filter((part) => Boolean(part));
    return present.length > 0 ? present.join(flagT('meta_separator', ' • ')) : undefined;
}
function assetMeta(assetName) {
    return assetName ? flagT('asset_meta', 'Asset: %{name}', { name: assetName }) : null;
}
function recurringFlagCopy(flag) {
    const { title, occurrencesCount, unresolvedCount, firstOccurrenceDate, assetName } = flag;
    const since = firstOccurrenceDate ? formatMonthYear(firstOccurrenceDate) : null;
    const days = firstOccurrenceDate ? daysSince(firstOccurrenceDate) : null;
    let lead = title
        ? flagT('recurring_deficiency_lead_titled', '"%{title}" flagged %{count} times', { title, count: occurrencesCount })
        : flagT('recurring_deficiency_lead_untitled', 'Deficiency flagged %{count} times', { count: occurrencesCount });
    if (since)
        lead = flagT('recurring_deficiency_since', '%{lead} since %{since}', { lead, since });
    // "No repair documented" is only true when every occurrence is still open.
    const description = unresolvedCount === occurrencesCount
        ? flagT('recurring_deficiency_status_all_open', '%{lead} — every deficiency still open, no repair documented', {
            lead
        })
        : flagT('recurring_deficiency_status_partial', '%{lead} — %{unresolved} of %{total} still open', {
            lead,
            unresolved: unresolvedCount,
            total: occurrencesCount
        });
    return {
        title: flagT('recurring_deficiency_title', 'Recurring Deficiency Pattern'),
        description,
        meta: joinMeta([
            days == null
                ? null
                : flagT('recurring_deficiency_oldest_occurrence', 'Oldest occurrence: %{count} days ago', { count: days }),
            assetMeta(assetName)
        ])
    };
}
function documentationGapsDescription(unansweredCount, missingNotesCount, sectionsCount) {
    let unanswered = '';
    if (unansweredCount > 0) {
        unanswered = flagT('documentation_gaps_unanswered', '%{count} unanswered questions', { count: unansweredCount });
        if (sectionsCount != null && sectionsCount > 0) {
            unanswered = flagT('documentation_gaps_unanswered_in_sections', '%{unanswered} in %{sections}', {
                unanswered,
                sections: flagT('documentation_gaps_sections', '%{count} sections', { count: sectionsCount })
            });
        }
    }
    const missingNotes = missingNotesCount > 0
        ? flagT('documentation_gaps_missing_notes', '%{count} deficiencies missing their notes and photos', {
            count: missingNotesCount
        })
        : '';
    if (unanswered && missingNotes) {
        return flagT('documentation_gaps_both', '%{unanswered} and %{missingNotes}', { unanswered, missingNotes });
    }
    return unanswered || missingNotes;
}
function documentationGapsFlagCopy(flag) {
    const occurredOn = flag.occurredAt ? formatUsDate(flag.occurredAt) : null;
    let lastOccurrence = null;
    if (flag.isCurrentInspection) {
        lastOccurrence = flagT('documentation_gaps_last_occurrence_current', 'Last occurrence: this inspection');
    }
    else if (occurredOn) {
        lastOccurrence = flagT('documentation_gaps_last_occurrence_date', 'Last occurrence: %{date}', {
            date: occurredOn
        });
    }
    return {
        title: flagT('documentation_gaps_title', 'Documentation Gaps'),
        description: documentationGapsDescription(flag.unansweredCount, flag.missingNotesCount, flag.unansweredSectionsCount),
        meta: joinMeta([lastOccurrence, assetMeta(flag.assetName)])
    };
}
function assetCountChangeFlagCopy(flag) {
    return {
        title: flagT('asset_count_change_title', 'Asset Count Change'),
        description: flagT('asset_count_change_desc', 'Asset count went %{priorCount} to %{currentCount} since %{sinceDate}', { priorCount: flag.priorCount, currentCount: flag.currentCount, sinceDate: flag.sinceDate })
    };
}
function performanceFlagCopy(flag) {
    switch (flag.type) {
        case 'recurring_deficiency_pattern':
            return recurringFlagCopy(flag);
        case 'documentation_gaps':
            return documentationGapsFlagCopy(flag);
        default:
            return assetCountChangeFlagCopy(flag);
    }
}
function PerformanceFlagsList({ flags }) {
    if (flags.length === 0)
        return null;
    return (React.createElement("section", { className: "technician-scorecard-drawer__section" },
        React.createElement(Text, { variant: "x-small", as: "h4", className: "technician-scorecard-drawer__section-title" }, translate('performance_flags', { namespace: NS, defaultValue: 'Performance Flags' })),
        React.createElement("ul", { className: "technician-scorecard-drawer__flags" }, flags.map((flag) => {
            const copy = performanceFlagCopy(flag);
            return (React.createElement("li", { key: flag.type },
                React.createElement(PerformanceFlagCard, { icon: PERFORMANCE_FLAG_ICONS[flag.type], title: copy.title, description: copy.description, meta: copy.meta })));
        }))));
}
function TechnicianScorecardDrawer({ technicianId, isOpen, onClose, inspectionId }) {
    var _a;
    const { data, isLoading } = useTechnicianScorecard(technicianId, isOpen, inspectionId);
    return (React.createElement(Brushaway, { controlledOpen: isOpen, onClose: onClose, minWidth: 960 },
        React.createElement(BrushawayHeader, { title: (_a = data === null || data === void 0 ? void 0 : data.name) !== null && _a !== void 0 ? _a : '' }),
        React.createElement(BrushawayBody, { className: "technician-scorecard-drawer" },
            isLoading && (React.createElement(Text, { variant: "body", as: "p" }, translate('loading', { namespace: NS, defaultValue: 'Loading scorecard...' }))),
            (data === null || data === void 0 ? void 0 : data.status) === 'pending' && (React.createElement(Text, { variant: "body", as: "p" }, translate('not_generated', {
                namespace: NS,
                defaultValue: 'A scorecard has not been generated for this technician yet.'
            }))),
            data && data.status !== 'pending' && (React.createElement(React.Fragment, null,
                data.currentRating && (React.createElement("div", { className: "technician-scorecard-drawer__performance" },
                    React.createElement(Text, { variant: "body", as: "span", className: "technician-scorecard-drawer__performance-label" }, translate('overall_performance', { namespace: NS, defaultValue: 'Overall Performance:' })),
                    React.createElement(Status, { label: technicianRatingLabel(data.currentRating), color: TECHNICIAN_RATING_STATUS_COLOR[data.currentRating] }))),
                React.createElement(KpiTileGrid, { kpis: data.kpis, kpiShowLines: KPI_ORDER_LINE_1 }),
                React.createElement("div", { className: "technician-scorecard-drawer__charts-row" },
                    React.createElement(IssueTypeBreakdownChart, { rows: data.issueTypeBreakdown }),
                    React.createElement(WeeklyIssuesTrendChart, { rows: data.weeklyIssuesTrend })),
                React.createElement(KpiTileGrid, { kpis: data.kpis, kpiShowLines: KPI_ORDER_LINE_2 }),
                React.createElement(PerformanceFlagsList, { flags: data.performanceFlags })))),
        React.createElement(BrushawayFooter, null,
            React.createElement(Button, { type: "button", variant: "default", onClick: onClose }, translate('close', { namespace: NS, defaultValue: 'Close' })))));
}
export default TechnicianScorecardDrawer;
